/*
    Vanilla replacements for the Iguana getters on Lesson.Stream.Chapter.

    The Iguana chapter walks back up to its stream to resolve lesson ids into lessons, so every
    function here takes the stream as well. Like the Iguana version, a lesson id that resolves to
    nothing is skipped rather than raising: an unpublished lesson may be missing from the response.
*/
import { type ChapterAttrs, type LessonForStreamDashboard, type StreamForStreamDashboard } from 'Lessons';
import { type ProgressStatusName } from '../StreamDashboard.types';
import { getLessonProgressStatusName, isLessonComplete } from './lessonSelectors';

/** Mirrors Chapter#lessonIds, which prefers lessonHashes over the legacy lessonIds field. */
export const getChapterLessonIds = (chapter: ChapterAttrs) =>
    chapter.lessonHashes ? chapter.lessonHashes.map(lessonHash => lessonHash.lessonId) : chapter.lessonIds;

/** Mirrors Chapter#lessons. */
export const getChapterLessons = (
    stream: StreamForStreamDashboard,
    chapter: ChapterAttrs,
): LessonForStreamDashboard[] =>
    getChapterLessonIds(chapter)
        .map(lessonId => stream.lessons.find(lesson => lesson.id === lessonId))
        .filter((lesson): lesson is LessonForStreamDashboard => !!lesson);

/** Mirrors Chapter#comingSoonLessons. */
export const getComingSoonLessonsInChapter = (
    stream: StreamForStreamDashboard,
    chapter: ChapterAttrs,
): LessonForStreamDashboard[] =>
    (chapter.lessonHashes ?? [])
        .filter(lessonHash => lessonHash.comingSoon)
        .map(lessonHash => stream.lessons.find(lesson => lesson.id === lessonHash.lessonId))
        .filter((lesson): lesson is LessonForStreamDashboard => !!lesson);

/** Mirrors Chapter#isLessonComingSoon, which indexes lessonHashes by the lesson's position. */
export const isLessonComingSoon = (chapter: ChapterAttrs, lesson: LessonForStreamDashboard) => {
    const index = getChapterLessonIds(chapter).indexOf(lesson.id);
    if (index === -1) return false;
    return chapter.lessonHashes?.[index]?.comingSoon === true;
};

/** Mirrors Chapter#progressStatus. An empty chapter is 'completed', as in the Iguana version. */
export const getChapterProgressStatusName = (
    stream: StreamForStreamDashboard,
    chapter: ChapterAttrs,
): ProgressStatusName => {
    const lessons = getChapterLessons(stream, chapter);
    const statuses = lessons.map(lesson => getLessonProgressStatusName(lesson));

    if (statuses.every(status => status === 'completed')) return 'completed';
    if (statuses.every(status => status === 'not_started')) return 'not_started';
    return 'in_progress';
};

export const isChapterComplete = (stream: StreamForStreamDashboard, chapter: ChapterAttrs) =>
    getChapterProgressStatusName(stream, chapter) === 'completed';

/** Mirrors Chapter#completedLessons. */
export const getCompletedLessonsInChapter = (stream: StreamForStreamDashboard, chapter: ChapterAttrs) =>
    getChapterLessons(stream, chapter).filter(lesson => isLessonComplete(lesson));

/** Mirrors Lesson#chapter, the reverse lookup from a lesson to the chapter that holds it. */
export const getChapterForLesson = (
    stream: StreamForStreamDashboard,
    lesson: LessonForStreamDashboard,
): ChapterAttrs | undefined => stream.chapters.find(chapter => getChapterLessonIds(chapter).includes(lesson.id));

/** Mirrors Lesson#chapterIndex. */
export const getChapterIndexForLesson = (stream: StreamForStreamDashboard, lesson: LessonForStreamDashboard) => {
    const chapter = getChapterForLesson(stream, lesson);
    return chapter ? stream.chapters.indexOf(chapter) : undefined;
};

/** Mirrors Lesson#chapterLessonsIndex, the lesson's position within its own chapter. */
export const getChapterLessonsIndex = (stream: StreamForStreamDashboard, lesson: LessonForStreamDashboard) => {
    const chapter = getChapterForLesson(stream, lesson);
    return chapter ? getChapterLessonIds(chapter).indexOf(lesson.id) : undefined;
};
